'use client';

import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { X } from 'lucide-react';
import { ChevronDownIcon } from '../icons';

export interface SelectOption {
  id: number | string;
  label: string;
  description?: string;
}

export interface SelectProps {
  options: SelectOption[];
  value: number | string | null;
  onChange: (value: number | string | null) => void;
  placeholder?: string;
  label?: string;
  description?: string;
  direction?: 'vertical' | 'horizontal';
  disabled?: boolean;
  isLoading?: boolean;
  emptyMessage?: string;
  required?: boolean | { required: boolean; message: string };
  clearable?: boolean;
  searchable?: boolean;
}

export const Select = ({
  options,
  value,
  onChange,
  placeholder = 'Select an option...',
  label,
  description,
  direction = 'vertical',
  disabled = false,
  isLoading = false,
  emptyMessage = 'No options available.',
  required = false,
  clearable = true,
  searchable = false
}: SelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen){
      setSearchTerm('');
      return;
    }

    searchInputRef.current?.focus();
  
    const container = containerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const estimatedMenuHeight = 350;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    setOpenUp(spaceBelow < estimatedMenuHeight && spaceAbove > spaceBelow);
  }, [isOpen]);

  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return options;
    const term = searchTerm.toLowerCase();
    return options.filter((opt) => 
      opt.label.toLowerCase().includes(term) || 
      String(opt.id).toLowerCase().includes(term) ||
      (opt.description?.toLowerCase().includes(term) ?? false)
    );
  }, [options, searchTerm]);

  const selectedOption = options.find((opt) => opt.id === value);
  const displayText = selectedOption ? selectedOption.label : placeholder;
  
  const isRequired = typeof required === 'boolean' ? required : required?.required;
  const requiredMessage = typeof required === 'object' ? required.message : 'This field is required';
  const [isInvalid, setIsInvalid] = useState(false);
  const hiddenInputRef = useRef<HTMLInputElement>(null);

  const handleInvalid = useCallback((e: Event) => {
    setIsInvalid(true);
    e.preventDefault();
  }, []);

  useEffect(() => {
    const hiddenInput = hiddenInputRef.current;
    if (hiddenInput && isRequired) {
      hiddenInput.addEventListener('invalid', handleInvalid);
      return () => hiddenInput.removeEventListener('invalid', handleInvalid);
    }
  }, [isRequired, handleInvalid]);

  // Update hidden input validity
  useEffect(() => {
    if (hiddenInputRef.current && isRequired) {
      if (!value) {
        hiddenInputRef.current.setCustomValidity('This field is required');
      } else {
        hiddenInputRef.current.setCustomValidity('');
        setIsInvalid(false);
      }
    }
  }, [value, isRequired]);

  const control = (
    <div className="relative">
      <div
        className={`w-full flex items-center justify-between rounded-md border border-[#d9e0e3] px-2.5 py-1.5 text-[14px] text-[#2d3a3f] transition-colors ${
          disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:border-[#079a95]/40'
        } ${isOpen ? 'border-[#079a95] ring-2 ring-[#079a95]/20' : ''}`}
      >
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className="flex-1 flex items-center text-left min-w-0"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
        >
          <div className="flex-1 flex items-center text-left min-w-0">
            {(!isOpen || !searchable) && (
              <span
                className={`block w-full truncate text-[14px] ${selectedOption ? 'text-[#2d3a3f]' : 'text-[#97a7ad]'}`}
                title={selectedOption ? displayText : undefined}
              >
                {isLoading ? 'Loading…' : displayText}
              </span>
            )}
            <input
              ref={searchInputRef}
              type="text"
              value={isOpen && searchable ? searchTerm : ''}
              onChange={(e) => searchable && setSearchTerm(e.target.value)}
              placeholder={isLoading ? 'Loading...' : displayText}
              className={`w-full bg-transparent border-none outline-none focus:outline-none focus:ring-0 text-[14px] text-[#2d3a3f] placeholder:text-[#97a7ad] p-0 h-auto ${!isOpen || !searchable ? 'hidden' : ''}`}
              readOnly={!isOpen || !searchable}
              onClick={() => !disabled && setIsOpen(true)}
            />
          </div>
        </button>
        <div className="flex items-center gap-1 shrink-0">
          {selectedOption && clearable && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onChange(null);
              }}
              disabled={disabled}
              className="p-0.5 text-[#5a717a] hover:text-[#2d3a3f] transition-colors"
              aria-label="Clear selection"
            >
              <X className="w-4 h-4" />
            </button>
          )}
          <button
            type="button"
            onClick={() => !disabled && setIsOpen(!isOpen)}
            disabled={disabled}
            className="p-0.5 text-[#5a717a] transition-transform duration-200"
            aria-label="Toggle dropdown"
          >
            <ChevronDownIcon className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </button>
        </div>
      </div>

      {isOpen && (
        <div
          className={`absolute left-0 z-20 max-h-64 w-max min-w-full max-w-[min(28rem,90vw)] overflow-y-auto rounded-lg border border-[#d9e0e3] bg-white shadow-lg ${
            openUp ? 'bottom-full mb-2' : 'top-full mt-2'
          }`}
        >
          <div className="space-y-1 p-1">
            {isLoading ? (
              <div className="p-2 text-center text-[12px] text-[#5a717a]">
                Loading…
              </div>
            ) : options.length === 0 ? (
              <div className="p-2 text-[12px] text-[#5a717a]">
                {emptyMessage}
              </div>
            ) : filteredOptions.length === 0 ? (
              <div className="p-2 text-[12px] text-[#5a717a]">
                No results found
              </div>
            ) : (
              filteredOptions.map((option) => {
                const isSelected = value === option.id;
                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => {
                      onChange(option.id);
                      setIsOpen(false);
                    }}
                    className={`w-full flex items-center justify-between rounded-lg px-2 py-2 text-left transition-colors ${
                      isSelected
                        ? 'bg-[#079a95]/10 text-[#079a95]'
                        : 'text-[#2d3a3f] hover:bg-[#f5f7f8]'
                    }`}
                  >
                    <div className="flex flex-col flex-1 min-w-0">
                      <span className="font-roboto text-[14px] truncate" title={option.label}>
                        {option.label}
                      </span>
                      {option.description && (
                        <span className="text-[12px] text-[#5a717a] truncate" title={option.description}>{option.description}</span>
                      )}
                    </div>
                    {isSelected && (
                      <div className="ml-2 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#079a95]">
                        <svg width="12" height="9" viewBox="0 0 12 9" fill="none">
                          <path
                            d="M1 4.5L4.5 8L11 1"
                            stroke="white"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );

  if (direction === 'horizontal') {
    return (
      <div className="flex items-start gap-2" ref={containerRef}>
        {label && (
          <label className="font-roboto text-[14px] text-[#2d3a3f] font-medium text-sm pt-[6px]">
            {label}
            {isRequired && <span className="text-[#2d3a3f] ml-1">*</span>}
          </label>
        )}
        <div className="flex-1">
          {description && <p className="text-[12px] text-[#5a717a] mb-1">{description}</p>}
          {control}
          {isRequired && isInvalid && <p className="text-[12px] text-red-500">{requiredMessage}</p>}
          {isRequired && <input ref={hiddenInputRef} type="hidden" required />}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2" ref={containerRef}>
      {label && (
        <div>
          <label className="font-roboto text-[14px] text-[#2d3a3f] font-medium text-sm">
            {label}
            {isRequired && <span className="text-[#2d3a3f] ml-1">*</span>}
          </label>
          {description && <p className="text-[12px] text-[#5a717a]">{description}</p>}
        </div>
      )}
      {control}
      {isRequired && isInvalid && <p className="text-[12px] text-red-500">{requiredMessage}</p>}
      {isRequired && <input ref={hiddenInputRef} type="hidden" required />}
    </div>
  );
};
